import React, { useState, useCallback } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Drawer,
  Grid,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { fetchPilots, fetchPilotById, fetchListingsByOwner, fetchPilotPreferences } from '../api/services';
import { useDebounce, useFetch } from '../hooks';
import {
  EmptyState,
  ErrorDisplay,
  LoadingSpinner,
  PermissionChip,
  PaginationControl,
  SectionHeader,
  PilotCard,
  PilotFilterSidebar,
  ContactItem,
  ListingCard,
  LocationMap,
  ImageLightbox,
  getPreferenceIcon,
} from '../components/common';
import { DESIGN_TOKENS } from '../styles/theme';
import type { PilotFilters } from '../types';
import { resolveAssetUrl } from '../utils';
import { useI18n } from '../i18n';

// ============================================================
// PILOTS CATALOG PAGE
// ============================================================

export function PilotsPage() {
  const { t } = useI18n();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

  const [searchInputValue, setSearchInputValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState<PilotFilters>({ active: true, pageSize: 12 });
  const [isMobileFilterDrawerOpen, setIsMobileFilterDrawerOpen] = useState(false);

  const debouncedSearchQuery = useDebounce(searchInputValue, 400);

  const appliedFilters: PilotFilters = {
    ...activeFilters,
    searchQuery: debouncedSearchQuery || undefined,
    page: currentPage,
  };

  const { data: pilotsData, isLoading, error, refetch } = useFetch(
    () => fetchPilots(appliedFilters),
    [debouncedSearchQuery, currentPage, activeFilters]
  );

  const handleFiltersChange = useCallback((newFilters: Partial<PilotFilters>) => {
    setActiveFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  }, []);

  const totalPages = pilotsData?.meta?.pagination?.pageCount ?? 1;
  const totalItems = pilotsData?.meta?.pagination?.total ?? 0;

  return (
    <Box sx={{ backgroundColor: DESIGN_TOKENS.colors.background, minHeight: '60vh' }}>
      {/* Page Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter}, ${DESIGN_TOKENS.colors.surface})`,
          borderBottom: `1px solid ${DESIGN_TOKENS.colors.border}`,
          py: 5,
        }}
      >
        <Container maxWidth="xl">
          <SectionHeader
            title={t('pilots.pageTitle')}
            subtitle={t('pilots.pageSubtitle')}
          />
          <Box sx={{ maxWidth: 640 }}>
            <TextField
              fullWidth
              placeholder={t('pilots.searchPlaceholder')}
              value={searchInputValue}
              onChange={(event) => setSearchInputValue(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') setCurrentPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: DESIGN_TOKENS.colors.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={{ backgroundColor: DESIGN_TOKENS.colors.surface }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Sidebar */}
          {!isMobileView && (
            <Grid item md={3} lg={2.5}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 80,
                  backgroundColor: DESIGN_TOKENS.colors.surface,
                  borderRadius: DESIGN_TOKENS.borderRadius.card,
                  border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${DESIGN_TOKENS.colors.border}`, backgroundColor: DESIGN_TOKENS.colors.primaryLighter }}>
                  <Typography variant="h6">{t('filters.title')}</Typography>
                </Box>
                <PilotFilterSidebar filters={activeFilters} onFiltersChange={handleFiltersChange} />
              </Box>
            </Grid>
          )}

          {/* Main content */}
          <Grid item xs={12} md={9} lg={9.5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {isLoading ? t('common.loading') : t('pilots.resultsSummary', { total: totalItems })}
              </Typography>
              {isMobileView && (
                <Button startIcon={<FilterListIcon />} variant="outlined" size="small" onClick={() => setIsMobileFilterDrawerOpen(true)}>
                  {t('filters.title')}
                </Button>
              )}
            </Box>

            {error && <ErrorDisplay message={error} onRetry={refetch} />}

            {!error && (
              <Grid container spacing={2.5}>
                {isLoading
                  ? Array.from({ length: 12 }).map((_, index) => (
                      <Grid item xs={12} sm={6} xl={4} key={`skeleton-${index}`}>
                        <Card sx={{ height: '100%' }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Skeleton variant="circular" width={52} height={52} />
                              <Box sx={{ flex: 1 }}>
                                <Skeleton variant="text" height={24} width="80%" />
                                <Skeleton variant="text" height={20} width="40%" />
                              </Box>
                            </Box>
                            <Skeleton variant="rounded" height={22} width="60%" />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))
                  : pilotsData?.data?.map((pilot) => (
                      <Grid item xs={12} sm={6} xl={4} key={pilot.id}>
                        <PilotCard pilot={pilot} />
                      </Grid>
                    ))}
              </Grid>
            )}

            {!isLoading && !error && pilotsData?.data?.length === 0 && (
              <EmptyState
                title={t('companies.noPilotsTitle')}
                description={t('pilots.noPilotsLong')}
                actionLabel={t('filters.clear')}
                onAction={() => { setSearchInputValue(''); setActiveFilters({ active: true, pageSize: 12 }); setCurrentPage(1); }}
              />
            )}

            <PaginationControl currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={activeFilters.pageSize} onPageChange={setCurrentPage} />
          </Grid>
        </Grid>
      </Container>

      <Drawer
        anchor="bottom"
        open={isMobileFilterDrawerOpen}
        onClose={() => setIsMobileFilterDrawerOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '80vh' } }}
      >
        <PilotFilterSidebar filters={activeFilters} onFiltersChange={handleFiltersChange} onClose={() => setIsMobileFilterDrawerOpen(false)} />
      </Drawer>
    </Box>
  );
}

// ============================================================
// PILOT DETAIL PAGE
// ============================================================

export function PilotDetailPage() {
  const { t } = useI18n();
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxIndex, setLightboxIndex] = React.useState(0);
  const [lightboxImages, setLightboxImages] = React.useState<{ url: string; name?: string }[]>([]);

  const { pilotId } = useParams<{ pilotId: string }>();
  const numericPilotId = pilotId ?? "";

  const { data: pilotData, isLoading: isPilotLoading, error: pilotError, refetch } = useFetch(
    () => fetchPilotById(numericPilotId),
    [numericPilotId],
    { enabled: numericPilotId != undefined }
  );

  const pilot = pilotData?.data;
  const ownerId = pilot?.documentId;

  const {
    data: listingsData,
    isLoading: isListingsLoading,
  } = useFetch(
    () => fetchListingsByOwner(ownerId!),
    [ownerId],
    { enabled: Boolean(ownerId) }
  );

  const {
    data: preferencesData,
    isLoading: isPreferencesLoading,
  } = useFetch(
    () => fetchPilotPreferences(ownerId!),
    [ownerId],
    { enabled: Boolean(ownerId) }
  );

  if (isPilotLoading) return <LoadingSpinner message={t('pilots.loadingProfile')} fullHeight />;

  if (pilotError || !pilot) {
    return (
      <Container maxWidth="xl" sx={{ py: 8 }}>
        <ErrorDisplay message={pilotError ?? t('pilots.notFound')} onRetry={refetch} />
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button component={RouterLink} to="/companies/pilots" startIcon={<ArrowBackIcon />} variant="outlined">
            {t('pilots.backToCatalog')}
          </Button>
        </Box>
      </Container>
    );
  }

  const { personal_data, company_information, active, permissions } = pilot;
  const contact_details = company_information.contact_details;
  const brand_graphics = company_information?.brand_graphics;
  const company_data = company_information?.company_data;
  const tax_information = company_information?.tax_information;

  const fullName = company_data?.company_name || `${personal_data.name} ${personal_data.surname}`;
  const initials = `${personal_data.name[0] ?? ''}${personal_data.surname[0] ?? ''}`.toUpperCase();

  const logoUrl = resolveAssetUrl(brand_graphics?.logo?.url);
  const coverUrl = resolveAssetUrl(brand_graphics?.coverImage?.url);

  const openLightbox = (images: { url: string; name?: string }[], index: number) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <Box sx={{ backgroundColor: DESIGN_TOKENS.colors.background, minHeight: '60vh' }}>
      {/* Cover Image */}
      <Box
        sx={{
          height: { xs: 180, md: 260 },
          backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
          backgroundImage: coverUrl ? `url(${coverUrl})` : `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter}, ${DESIGN_TOKENS.colors.primaryLight}40)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 60%)' }} />
      </Box>

      <Container maxWidth="xl">
        {/* Back button */}
        <Box sx={{ pt: 3, mb: 3 }}>
          <Button component={RouterLink} to="/companies/pilots" startIcon={<ArrowBackIcon />} variant="text" size="small">
            {t('pilots.backToCatalog')}
          </Button>
        </Box>

        <Grid container spacing={4}>
          {/* LEFT: Pilot Info Panel */}
          <Grid item xs={12} md={4} lg={3}>
            <Card sx={{ position: { md: 'sticky' }, top: 80, mt: { xs: 0, md: -10 }, zIndex: 10 }}>
              <CardContent sx={{ p: 3 }}>
                {/* Logo / Avatar */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '16px',
                    border: `3px solid ${DESIGN_TOKENS.colors.border}`,
                    backgroundColor: DESIGN_TOKENS.colors.surface,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    boxShadow: DESIGN_TOKENS.shadows.card,
                  }}
                >
                  {logoUrl ? (
                    <Box component="img" src={logoUrl} alt="Logo" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <Avatar sx={{ width: '100%', height: '100%', backgroundColor: DESIGN_TOKENS.colors.primary, fontSize: '1.75rem', fontWeight: 700, borderRadius: 0 }}>
                      {initials}
                    </Avatar>
                  )}
                </Box>

                {/* Name */}
                <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                  {fullName}
                </Typography>

                {/* Type & Status */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: active ? DESIGN_TOKENS.colors.success : DESIGN_TOKENS.colors.textDisabled }} />
                    <Typography variant="caption" color={active ? 'success.main' : 'text.disabled'} fontWeight={600}>
                      {active ? t('common.active') : t('common.inactive')}
                    </Typography>
                  </Box>
                </Box>

                {/* Permissions Sidebar Section */}
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>{t('filters.permissions')}</Typography>
                {permissions.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {permissions.map((permission) => (
                      <PermissionChip key={permission} permission={permission} />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>{t('pilots.noPermissions')}</Typography>
                )}

                {/* Description */}
                {company_data?.description && (
                  <>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, mb: 2 }}>
                      {company_data.description}
                    </Typography>
                  </>
                )}

                <Divider sx={{ mb: 2 }} />

                {/* Contact details */}
                {contact_details?.email && (
                  <ContactItem
                    icon={<EmailIcon fontSize="small" color="primary" />}
                    label={t('common.email')}
                    value={contact_details.email}
                    href={`mailto:${contact_details.email}`}
                  />
                )}
                {contact_details?.phone_number && (
                  <ContactItem
                    icon={<PhoneIcon fontSize="small" color="primary" />}
                    label={t('common.phone')}
                    value={contact_details.phone_number}
                    href={`tel:${contact_details.phone_number}`}
                  />
                )}
                {contact_details?.website_url && (
                  <ContactItem
                    icon={<LanguageIcon fontSize="small" color="primary" />}
                    label={t('common.website')}
                    value={contact_details.website_url.replace(/^https?:\/\//, '')}
                    href={contact_details.website_url}
                  />
                )}

                <Divider sx={{ my: 2 }} />

                {/* Tax information */}
                {tax_information?.tax_identification_number && (
                  <ContactItem
                    icon={<ReceiptIcon fontSize="small" color="primary" />}
                    label={t('common.taxId')}
                    value={tax_information.tax_identification_number}
                  />
                )}
                {tax_information?.regon && (
                  <ContactItem
                    icon={<ReceiptIcon fontSize="small" color="primary" sx={{ opacity: 0 }} />}
                    label={t('common.regon')}
                    value={tax_information.regon}
                  />
                )}

                <Divider sx={{ my: 2 }} />

                {/* Location information */}
                {contact_details?.location && (
                  <>
                    <ContactItem
                      icon={<LocationOnIcon fontSize="small" color="primary" />}
                      label={t('common.location')}
                      value={[contact_details.location.city, contact_details.location.country].filter(Boolean).join(', ') || 'Poland'}
                    />
                    <Box sx={{ height: 200, mb: 3, borderRadius: '8px', overflow: 'hidden', border: `1px solid ${DESIGN_TOKENS.colors.border}` }}>
                      <LocationMap location={contact_details.location} />
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>

          </Grid>

          {/* RIGHT: Content */}
          <Grid item xs={12} md={8} lg={9}>
            {/* Preferred Locations Section */}
            {!isPreferencesLoading && preferencesData?.data && preferencesData.data.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Typography variant="h3" sx={{ mb: 3 }}>
                  {t('account.preferences')}
                </Typography>
                <Grid container spacing={2}>
                  {preferencesData.data.map((pref) => (
                    <Grid item xs={12} sm={6} md={4} key={pref.id}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Box sx={{ p: 1, backgroundColor: DESIGN_TOKENS.colors.primaryLighter, borderRadius: '8px', display: 'flex' }}>
                            {getPreferenceIcon(pref.icon, 'small')}
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {pref.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {pref.location?.city || t('preferences.noLocation')}
                            </Typography>
                            <Typography variant="caption" color="primary" fontWeight={600} sx={{ mt: 0.5, display: 'block' }}>
                              {t('preferences.radius')}: {pref.distance / 1000} km
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Listings Section */}
            <Typography variant="h3" sx={{ mb: 3 }}>
              {t('companies.companyListings')}
            </Typography>

            {isListingsLoading && <LoadingSpinner message={t('account.loadingListings')} />}

            {!isListingsLoading && listingsData?.data && listingsData.data.length > 0 ? (
              <Grid container spacing={2.5} sx={{ mb: 6 }}>
                {listingsData.data.map((listing) => (
                  <Grid item xs={12} sm={6} key={listing.id}>
                    <ListingCard listing={listing} />
                  </Grid>
                ))}
              </Grid>
            ) : !isListingsLoading ? (
              <Box sx={{ mb: 6 }}>
                <EmptyState
                  title={t('companies.noCompanyListingsTitle')}
                  description={t('companies.noCompanyListingsDescription')}
                />
              </Box>
            ) : null}

            {/* Certificates section */}
            {company_data?.certificates && company_data.certificates.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Typography variant="h3" sx={{ mb: 3 }}>{t('companies.certificates')}</Typography>
                <Grid container spacing={2}>
                  {company_data.certificates.map((certificate, index) => {
                    const certUrl = resolveAssetUrl(certificate.url) || '';
                    const certs = company_data.certificates!.map(c => ({ url: resolveAssetUrl(c.url) || '', name: c.name }));
                    return (
                      <Grid item xs={12} sm={6} md={4} key={certificate.id}>
                        <Card variant="outlined" sx={{ height: '100%', cursor: 'pointer', '&:hover': { borderColor: DESIGN_TOKENS.colors.primary } }} onClick={() => openLightbox(certs, index)}>
                          <Box sx={{ height: 160, backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                            <Box component="img" src={certUrl} alt={certificate.name} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          </Box>
                          <CardContent sx={{ p: 2 }}>
                            <Typography variant="subtitle2" fontWeight={600} noWrap title={certificate.name}>
                              {certificate.name}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {/* Gallery section */}
            {brand_graphics?.gallery && brand_graphics.gallery.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Typography variant="h3" sx={{ mb: 3 }}>{t('companies.gallery')}</Typography>
                <Grid container spacing={2}>
                  {brand_graphics.gallery.map((galleryImage, index) => {
                    const galleryImages = brand_graphics.gallery!.map(img => ({ url: resolveAssetUrl(img.url) || '', name: img.name }));
                    return (
                      <Grid item xs={6} sm={4} md={3} key={galleryImage.id}>
                        <Box
                          onClick={() => openLightbox(galleryImages, index)}
                          sx={{
                            display: 'block',
                            aspectRatio: '1/1',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                          }}
                        >
                          <Box
                            component="img"
                            src={resolveAssetUrl(galleryImage.url)}
                            alt={galleryImage.name}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s ease', '&:hover': { transform: 'scale(1.05)' } }}
                          />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {/* CTA section */}
            <Box sx={{ p: 4, backgroundColor: DESIGN_TOKENS.colors.primaryLighter, borderRadius: DESIGN_TOKENS.borderRadius.card, textAlign: 'center', border: `1px solid ${DESIGN_TOKENS.colors.primary}20` }}>
              <PersonIcon sx={{ fontSize: 48, color: DESIGN_TOKENS.colors.primary, mb: 1.5 }} />
              <Typography variant="h4" fontWeight={700} gutterBottom>
                {t('pilots.collaborateTitle')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
                {t('pilots.collaborateDescription', { name: personal_data.name })}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                {contact_details?.phone_number && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<PhoneIcon />}
                    href={`tel:${contact_details.phone_number}`}
                    sx={{ px: 4, minWidth: 160 }}
                  >
                    {t('pilots.call')}
                  </Button>
                )}
                {contact_details?.email && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    startIcon={<EmailIcon />}
                    href={`mailto:${contact_details.email}`}
                    sx={{ px: 4, minWidth: 160, backgroundColor: DESIGN_TOKENS.colors.surface }}
                  >
                    {t('pilots.sendMessage')}
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>

        <ImageLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
        />
      </Container>
    </Box>
  );
}
